// janado blob field: one full-screen WebGL2 canvas behind the page.
// Every element with [data-blob="<id>"] owns a liquid blob that follows its on-screen rect with spring physics,
// merges with its neighbours (smooth union) and stretches with its velocity.
// Choreography is done by animating the DOM anchors (CSS, GSAP); the field just follows.
//
// Anchor attributes:
//   data-blob="<id>"          required, unique per page; the same id on the next page = the blob travels
//   data-blob-color           'yellow' (default) | 'lavender' (trade-in) | 'sky' (trust) | '#hex'; inherited from the nearest ancestor
//   data-blob-scale           radius multiplier, animate it for pops (0 = gone)
//   data-blob-goo             how strongly it melts into neighbours (default 1)
//   data-blob-css             on touch devices drawn by CSS instead (product cards in grids), the field ignores it
//                             there; a flood can still start from it (motion.js passes its rect). Mouse: field as usual.
//   data-blob-motion          on an ancestor: 'story' (default, full jelly) | 'calm' (shop: rides the scroll)
//
// Boots itself after the page has loaded (never competes with LCP) and exposes window.janado.field.

const MAX = 40

/** Look tokens: one source for every page, every blob. */
export const BLOB_LOOK = {
  shadow: 0.035, // contact shadow strength on paper (0 = none)
  shadowX: 4, // shadow offset in px
  shadowY: 9,
  shadowSoft: 22, // shadow falloff in px
  shade: 0.06, // 3D: darkening of the lower-right rim
  shine: 0.11, // 3D: highlight on the upper-left rim
}

/** Motion tokens: share of its anchor's movement (scroll, sticky, layout) a blob rides along with — no lag, no stretch. */
export const BLOB_MOTION = { story: 0, calm: 0.9 }

const f1 = (n) => n.toFixed(3)

const vert = `#version 300 es
in vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`

const frag = `#version 300 es
precision highp float;
out vec4 o;
uniform vec4 uA[${MAX}];   // x, y (css px, top-left origin), radius, seed
uniform vec4 uB[${MAX}];   // vx, vy (px/s), goo, visibility
uniform vec3 uC[${MAX}];   // per-blob colour
uniform int uCount;
uniform vec2 uRes;         // css px
uniform float uDpr, uTime, uGoo, uWobble;
uniform vec3 uBg;
uniform float uDark;       // 0 = paper, 1 = lights off: blobs glow instead of casting shadows

vec3 gCol;
float field(vec2 p) {
  float d = 1e5, cw = 0.0;
  vec3 cm = vec3(0.0);
  for (int i = 0; i < ${MAX}; i++) {
    if (i >= uCount) break;
    vec4 a = uA[i], b = uB[i];
    float r = a.z * b.w;
    if (r < 0.5) continue;
    vec2 q = p - a.xy;
    // Stretch along velocity, volume-preserving; big blobs are heavier and stretch less.
    float sp = length(b.xy);
    if (sp > 1.0) {
      vec2 dir = b.xy / sp;
      float s = 1.0 + min(sp * 0.0009 * (0.5 + a.w) * clamp(110.0 / r, 0.25, 1.0), 0.35);
      float along = dot(q, dir);
      vec2 perp = q - dir * along;
      q = dir * (along / s) + perp * sqrt(s);
    }
    // Too far away to change this pixel: skip the wobble trig and the colour weight.
    float k = uGoo * clamp(r / 60.0, 0.35, 1.0) * (b.z > 0.0 ? b.z : 1.0);
    float dr = length(q) - r * 1.03;
    if (dr > 140.0 && dr > d + k) continue;
    float ang = atan(q.y, q.x);
    float t = uTime + a.w * 10.0;
    float wob = (sin(ang * 3.0 + t * 0.9) * 0.6 + sin(ang * 5.0 - t * 1.3 + a.w * 6.0) * 0.4) * uWobble;
    float di = length(q) - r * (1.0 + 0.028 * wob);
    float h = max(k - abs(d - di), 0.0) / k; // polynomial smooth-min
    d = min(d, di) - h * h * k * 0.25;
    float w = exp(-max(di, 0.0) * 0.03) / (1.0 + max(di, 0.0));
    cw += w; cm += w * uC[i];
  }
  gCol = cw > 0.0 ? cm / cw : vec3(1.0, 0.89, 0.0);
  return d;
}

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
  vec2 px = vec2(gl_FragCoord.x, uRes.y * uDpr - gl_FragCoord.y) / uDpr;
  float d = field(px);
  vec3 fc = gCol;
  // Soft contact shadow, the field sampled down-right. Dark pages glow instead, so skip the second pass there.
  float ds = uDark < 0.999 ? field(px - vec2(${f1(BLOB_LOOK.shadowX)}, ${f1(BLOB_LOOK.shadowY)})) : 1e5;
  vec3 col = uBg * (1.0 - ${f1(BLOB_LOOK.shadow)} * (1.0 - uDark) * smoothstep(${f1(BLOB_LOOK.shadowSoft)}, -8.0, ds));
  col += fc * 0.22 * uDark * exp(-max(d, 0.0) / 60.0);
  // Gummy depth from the SDF gradient: lit upper-left rim, shaded lower-right rim.
  vec3 blob = fc;
  vec2 g = vec2(dFdx(d), -dFdy(d));
  vec2 n = length(g) > 1e-4 ? normalize(g) : vec2(0.0);
  float rim = smoothstep(-30.0, 0.0, d);
  vec2 L = normalize(vec2(-0.55, 0.85));
  blob *= 1.0 - ${f1(BLOB_LOOK.shade)} * rim * max(dot(n, -L), 0.0);
  blob += vec3(1.0) * ${f1(BLOB_LOOK.shine)} * pow(rim, 2.0) * pow(max(dot(n, L), 0.0), 3.0);
  col = mix(col, blob, smoothstep(0.8, -0.8, d * uDpr / max(uDpr, 1.0)));
  col += (hash(px + fract(uTime) * 91.7) - 0.5) * 0.028; // paper grain
  o = vec4(col, 1.0);
}`

const hex = (h) => {
  const n = parseInt(h.replace('#', ''), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}
const css = (name, fallback) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
const COLORS = {}
const colorOf = (v) => hex(v === 'lavender' ? COLORS.lavender : v === 'sky' ? COLORS.sky : !v || v === 'yellow' ? COLORS.yellow : v)
const tint = (el) => el.closest('[data-blob-color]')?.dataset.blobColor

// Touch devices draw [data-blob-css] anchors with CSS (product cards); with a mouse the field draws them too.
const CSS_BLOBS = !document.documentElement.classList.contains('fine')

const newBlob = (id, x, y, r, color, extra = {}) => ({
  id, el: null, x, y, r, vx: 0, vy: 0, vr: 0, tx: x, ty: y, tr: r, color: [...color], tcolor: color,
  seed: Math.random(), calm: 0, goo: 0, ax: x, ay: y, vis: 1, hold: null, ...extra,
})

export class BlobField {
  static create(canvas, reduced) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'high-performance' })
    return gl ? new BlobField(canvas, gl, reduced) : null
  }

  constructor(canvas, gl, reduced) {
    this.canvas = canvas
    this.gl = gl
    this.reduced = reduced
    this.blobs = new Map()
    this.mouse = { x: -999, y: -999, active: false }
    this.frameMs = 16.7
    this.minMs = Infinity // fastest frame interval seen, see start()
    this.dpr = 1
    this.dprScale = 1
    this.slow = 0
    this.t0 = performance.now()
    this.last = performance.now()
    this.raf = 0
    this.idle = false
    this.loc = {}
    this.A = new Float32Array(MAX * 4)
    this.B = new Float32Array(MAX * 4)
    this.C = new Float32Array(MAX * 3)

    const prog = gl.createProgram()
    for (const [type, src] of [[gl.VERTEX_SHADER, vert], [gl.FRAGMENT_SHADER, frag]]) {
      const s = gl.createShader(type)
      gl.shaderSource(s, src)
      gl.compileShader(s)
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader')
      gl.attachShader(prog, s)
    }
    gl.linkProgram(prog)
    gl.useProgram(prog)
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    for (const n of ['uA', 'uB', 'uC', 'uCount', 'uRes', 'uDpr', 'uTime', 'uGoo', 'uWobble', 'uBg', 'uDark']) this.loc[n] = gl.getUniformLocation(prog, n)

    COLORS.yellow = css('--yellow', '#ffe300')
    COLORS.lavender = css('--lavender', '#d3b0f5')
    COLORS.sky = css('--sky', '#97cadb')
    this.paper = hex(css('--paper', '#f7f3f2'))
    gl.uniform3fv(this.loc.uBg, this.paper)
    gl.uniform1f(this.loc.uGoo, 70)
    gl.uniform1f(this.loc.uWobble, reduced ? 0 : 1)
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault()
      this.stop()
      document.documentElement.classList.replace('webgl', 'no-webgl')
    })
    addEventListener('resize', () => this.resize())
    this.resize()
  }

  /** 0 = normal paper, 1 = lights off (campaign darkness): darker background, shadows turn into glow. */
  setDark(k) {
    const night = [0.075, 0.068, 0.068]
    this.gl.uniform3fv(this.loc.uBg, this.paper.map((c, i) => c + (night[i] - c) * k))
    this.gl.uniform1f(this.loc.uDark, k)
    this.idle = false
  }

  resize() {
    this.dpr = Math.max(0.75, Math.min(devicePixelRatio || 1, 2) * this.dprScale)
    this.canvas.width = Math.round(innerWidth * this.dpr)
    this.canvas.height = Math.round(innerHeight * this.dpr)
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height)
    this.idle = false
  }

  /**
   * Adopt all [data-blob] anchors under root. Anchors that left the page shrink away where they are.
   * instant: new blobs start at full size (hand-over from the CSS circles at boot) instead of growing in.
   */
  sync(root = document, instant = false) {
    const seen = new Set()
    for (const el of root.querySelectorAll(CSS_BLOBS ? '[data-blob]:not([data-blob-css])' : '[data-blob]')) {
      let id = el.dataset.blob
      // the same id twice on one page (the hero's device also shows as a card further down): one blob per id, so the
      // later anchor gets its own id, otherwise the hero blob would sit on the card. The first one (in page order) keeps it.
      if (seen.has(id)) {
        let n = 2
        while (seen.has(`${id}~${n}`)) n++
        id = el.dataset.blob = `${id}~${n}`
      }
      seen.add(id)
      const color = colorOf(tint(el))
      let b = this.blobs.get(id)
      if (!b) {
        const r = el.getBoundingClientRect()
        const size = instant && r.width ? (Math.min(r.width, r.height) / 2) * Number(el.dataset.blobScale || 1) : 0
        b = newBlob(id, r.left + r.width / 2, r.top + r.height / 2, size, color)
        this.blobs.set(id, b)
      }
      if (b.el !== el) {
        // a new anchor (e.g. after a page change): its jump is a journey, never carried by calm
        const r = el.getBoundingClientRect()
        b.ax = r.left + r.width / 2
        b.ay = r.top + r.height / 2
      }
      b.el = el
      b.goo = Number(el.dataset.blobGoo) || 0
      b.calm = BLOB_MOTION[el.closest('[data-blob-motion]')?.dataset.blobMotion || 'story'] ?? 0
      b.tcolor = color
      b.hold = null
    }
    for (const [id, b] of this.blobs) {
      if (!b.leaving) b.hold = null // a cover blob is released; the flood of a leaving page stays put
      if (!seen.has(id) && id !== 'cursor') b.el = null
    }
    this.idle = false
  }

  /** Grow a blob over the whole screen (page transition). Resolves when the screen is covered. */
  flood(id, from, c) {
    const color = colorOf(c)
    let b = this.blobs.get(id)
    if (!b) {
      b = newBlob(id, from?.x ?? innerWidth / 2, from?.y ?? innerHeight / 2, from?.r ?? 0, color)
      this.blobs.set(id, b)
    }
    b.el = null
    b.tcolor = color
    b.leaving = true
    const h = (b.hold = { x: b.x, y: b.y, r: b.r })
    const cover = Math.hypot(innerWidth, innerHeight) * 0.75
    this.idle = false
    return new Promise((res) => {
      const start = performance.now()
      const from0 = { ...h }
      const dur = this.reduced ? 250 : 750
      const step = () => {
        const k = Math.min((performance.now() - start) / dur, 1)
        const e = k < 0.5 ? 16 * k ** 5 : 1 - (-2 * k + 2) ** 5 / 2 // quint in-out
        h.x = from0.x + (innerWidth / 2 - from0.x) * e
        h.y = from0.y + (innerHeight / 2 - from0.y) * e
        h.r = from0.r + (cover - from0.r) * e
        if (k < 1) requestAnimationFrame(step)
        else res()
      }
      step()
    })
  }

  /** Instantly cover the screen with a blob; the next sync() lets it spring to its anchor or shrink away. */
  cover(id, c) {
    const r = Math.hypot(innerWidth, innerHeight) * 0.75
    const x = innerWidth / 2
    const y = innerHeight / 2
    this.blobs.set(id, newBlob(id, x, y, r, colorOf(c), { hold: { x, y, r } }))
    this.idle = false
  }

  start() {
    const loop = (now) => {
      this.raf = requestAnimationFrame(loop)
      if (document.hidden) return
      const dt = Math.min((now - this.last) / 1000, 0.05)
      this.frameMs += (now - this.last - this.frameMs) * 0.05
      // the display's own cadence (16.7 ms at 60 Hz, 8.3 at 120 Hz, 33 in iOS Low Power Mode) is the baseline
      if (now - this.last > 4) this.minMs = Math.min(this.minMs, now - this.last)
      this.last = now
      this.step(dt)
      // before drawing: resizing clears the canvas, after the draw that cleared frame would flash black
      this.govern()
      this.draw(now)
    }
    this.raf = requestAnimationFrame(loop)
  }

  stop() {
    cancelAnimationFrame(this.raf)
  }

  govern() {
    // Drop resolution when we fall behind the display's cadence for about a second; never below half.
    if (this.frameMs > this.minMs * 1.15) this.slow++
    else this.slow = Math.max(0, this.slow - 0.25)
    if (this.slow > 60 && this.dprScale > 0.5) {
      this.dprScale *= 0.85
      this.slow = 0
      this.resize()
    }
  }

  step(dt) {
    const k = 190
    const m = this.mouse
    for (const [id, b] of this.blobs) {
      if (b.hold) {
        b.vx = (b.hold.x - b.x) / Math.max(dt, 1e-3)
        b.vy = (b.hold.y - b.y) / Math.max(dt, 1e-3)
        b.x = b.hold.x
        b.y = b.hold.y
        b.r = b.hold.r
        b.vr = 0
        continue
      }
      if (b.el && b.el.isConnected) {
        const r = b.el.getBoundingClientRect()
        b.tcolor = colorOf(tint(b.el))
        if (r.width === 0 && r.height === 0) b.tr = 0 // hidden anchor: shrink in place
        else {
          const nx = r.left + r.width / 2
          const ny = r.top + r.height / 2
          // calm: ride the anchor's own movement; the spring only sees the leftover
          if (b.calm && b.tr > 0) {
            b.x += (nx - b.ax) * b.calm
            b.y += (ny - b.ay) * b.calm
          }
          b.ax = b.tx = nx
          b.ay = b.ty = ny
          const sc = Number(b.el.dataset.blobScale || 1)
          b.tr = (Math.min(r.width, r.height) / 2) * (Number.isFinite(sc) ? sc : 1) // a bad scale must never poison the spring
        }
      } else if (id !== 'cursor') b.tr = 0
      // Magnetism: blobs lean toward a nearby pointer.
      if (m.active && !this.reduced && id !== 'cursor' && b.tr > 0) {
        const dx = m.x - b.tx
        const dy = m.y - b.ty
        const pull = Math.max(0, 1 - Math.hypot(dx, dy) / (b.tr * 1.8)) * 0.07
        b.tx += dx * pull
        b.ty += dy * pull
      }
      if (this.reduced) {
        b.x = b.tx
        b.y = b.ty
        b.r = b.tr
        b.vx = b.vy = 0
        b.color = [...b.tcolor]
      } else {
        // Every blob has its own jelly: stiffness and damping derive from its seed.
        const kb = k * (0.7 + b.seed * 0.75)
        const cb = 2 * Math.sqrt(kb) * (0.48 + ((b.seed * 7.13) % 1) * 0.3)
        b.vx += ((b.tx - b.x) * kb - b.vx * cb) * dt
        b.x += b.vx * dt
        b.vy += ((b.ty - b.y) * kb - b.vy * cb) * dt
        b.y += b.vy * dt
        b.vr += ((b.tr - b.r) * kb * 0.7 - b.vr * cb * 0.8) * dt
        b.r = Math.max(0, b.r + b.vr * dt)
        const kc = Math.min(1, dt * 4)
        for (let j = 0; j < 3; j++) b.color[j] += (b.tcolor[j] - b.color[j]) * kc
      }
      if (!b.el && id !== 'cursor' && b.r < 0.5 && b.tr === 0) this.blobs.delete(id)
    }
  }

  setCursor(on) {
    if (!on) return this.blobs.delete('cursor')
    if (!this.blobs.has('cursor')) this.blobs.set('cursor', newBlob('cursor', -99, -99, 0, colorOf(), { seed: 0.3 }))
  }

  cursorTarget(x, y, r) {
    const b = this.blobs.get('cursor')
    if (b) {
      b.tx = x
      b.ty = y
      b.tr = r
    }
  }

  draw(now) {
    const gl = this.gl
    let n = 0
    for (const b of this.blobs.values()) {
      if (n >= MAX) break
      if (b.r < 0.5 || b.y + b.r < -120 || b.y - b.r > innerHeight + 120 || b.x + b.r < -120 || b.x - b.r > innerWidth + 120) continue
      this.A.set([b.x, b.y, b.r, b.seed], n * 4)
      this.B.set([b.vx, b.vy, b.goo, b.vis], n * 4)
      this.C.set(b.color, n * 3)
      n++
    }
    // No blob on screen: the canvas keeps its last (empty) frame, no GPU work until one shows up.
    if (!n && this.idle) return
    this.idle = !n
    gl.uniform4fv(this.loc.uA, this.A)
    gl.uniform4fv(this.loc.uB, this.B)
    gl.uniform3fv(this.loc.uC, this.C)
    gl.uniform1i(this.loc.uCount, n)
    gl.uniform2f(this.loc.uRes, innerWidth, innerHeight)
    gl.uniform1f(this.loc.uDpr, this.dpr)
    gl.uniform1f(this.loc.uTime, this.reduced ? 0 : (now - this.t0) / 1000)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }
}

// ---------- Boot ----------
const html = document.documentElement
const reduced = html.classList.contains('reduced')
window.janado = window.janado || {}

/**
 * Arrival after a page transition (assets/motion.js floods the screen before leaving, layout/theme.liquid marks
 * the new page with html.arrive and a CSS colour cover). The field takes the cover over with a blob of the same
 * colour, then lets it flow back: into the anchor with the same id (shared element) or away.
 */
/** The anchor the cover flows into: the one with the flood's id (shared element, e.g. the product blob), else the
 *  biggest blob anchor on screen (usually the hero), so the colour always pours into the new page like in the
 *  prototype instead of shrinking away on its own. */
function landing(id) {
  const same = id && document.querySelector(`[data-blob="${CSS.escape(id)}"]`)
  if (same) return same
  let best = null
  let size = 0
  // only page content (not the header's cart blob), only anchors that are meant to be seen
  for (const el of document.querySelectorAll(CSS_BLOBS ? '#MainContent [data-blob]:not([data-blob-css])' : '#MainContent [data-blob]')) {
    if (el.dataset.blobScale === '0') continue
    const r = el.getBoundingClientRect()
    const s = Math.min(el.offsetWidth, el.offsetHeight) // layout size: a paused pop-in (scale 0) doesn't count as small
    if (s > size && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth) (best = el), (size = s)
  }
  return best
}

function arrive(field) {
  const a = window.janado.arrival
  if (!field) return html.classList.remove('arrive', 'soft')
  if (!a) {
    // soft arrival (back/forward, see layout/theme.liquid): the blobs grow from 0 (boot synced them that way);
    // once the page is laid out, let the content rise in
    if (!html.classList.contains('soft')) return html.classList.remove('arrive', 'soft')
    const t0 = performance.now()
    const ready = () => document.readyState === 'complete' || [...document.querySelectorAll('#MainContent [data-blob]')].some((el) => el.offsetWidth > 0) || performance.now() - t0 > 1500
    const wait = () => (ready() ? (html.classList.add('blobs-on'), html.classList.remove('arrive', 'soft'), field.sync()) : requestAnimationFrame(wait))
    return requestAnimationFrame(wait)
  }
  window.janado.arrival = null
  if (!field.blobs.get(a.id)?.hold) field.cover(a.id || 'arrive', a.c) // (back/forward: the flood is still there)
  const t0 = performance.now()
  // The field boots before the sections' CSS has arrived: anchors still measure 0 and the cover would pour into
  // nothing, with the page's blobs growing in a moment later. So wait under the cover until the page is laid out
  // (a sized blob anchor in the content, or the document fully loaded), at most 1.5 s.
  const ready = () =>
    document.readyState === 'complete' ||
    [...document.querySelectorAll('#MainContent [data-blob]')].some((el) => el.offsetWidth > 0) ||
    performance.now() - t0 > 1500
  const release = () => {
    const target = landing(a.id)
    const id = target?.dataset.blob
    if (id && id !== a.id) {
      // the cover becomes the landing anchor's blob, so it flows into it
      const b = field.blobs.get(a.id || 'arrive')
      field.blobs.delete(a.id || 'arrive')
      field.blobs.delete(id)
      if (b) field.blobs.set(id, Object.assign(b, { id }))
    }
    html.classList.add('blobs-on') // no CSS stand-in circles for a frame: the field draws from here
    html.classList.remove('arrive', 'soft') // the CSS cover goes; the blob of the same colour is already under it
    // The landing anchor must not play its own pop-in from 0: the cover shrinks into it instead.
    target?.getAnimations().forEach((x) => x.finish())
    field.sync() // released: the cover flows into its anchor while the other new blobs grow out of it
  }
  const wait = () => (ready() ? release() : requestAnimationFrame(wait))
  requestAnimationFrame(wait)
}

function boot() {
  const canvas = document.querySelector('.blob-field')
  let field = null
  if (canvas && html.classList.contains('webgl')) {
    try {
      field = BlobField.create(canvas, reduced)
    } catch (e) {
      console.warn('[blob-field]', e)
    }
  }
  if (!field) {
    html.classList.replace('webgl', 'no-webgl')
    html.classList.remove('arrive', 'soft') // the CSS cover fades out on its own
    return
  }
  window.janado.field = field
  // Arriving, the new blobs grow out of the cover (like the prototype); otherwise they take over the CSS circles.
  field.sync(document, !html.classList.contains('arrive'))
  field.start()
  arrive(field)
  addEventListener('janado:arrive', () => arrive(field)) // a prerendered page becoming visible
  // Back/forward (bfcache): the page comes back as we left it, covered by its own flood. Like an arrival, the
  // flood flows into the page's main blob while the content rises in (the prototype animates "back" too).
  addEventListener('pageshow', (e) => {
    if (!e.persisted) return
    const flood = [...field.blobs.values()].find((b) => b.leaving)
    html.classList.remove('leaving')
    if (!flood) return field.sync()
    html.classList.add('arrive') // content hidden, without a transition; removing it lets the content rise in
    field.blobs.delete(flood.id)
    window.janado.arrival = { id: flood.id, c: '' }
    flood.leaving = false
    field.blobs.set(flood.id, flood) // keeps its held, screen-filling shape and colour
    arrive(field)
  })
  window.dispatchEvent(new CustomEvent('janado:field'))
  // The first WebGL frame is drawn: now hide the CSS stand-in circles (same size, same place: no jump).
  requestAnimationFrame(() => requestAnimationFrame(() => html.classList.add('blobs-on')))

  // New, removed or changed anchors (theme editor, cart drawer, filters, sections loaded later): re-sync once per frame.
  let queued = false
  const resync = () => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => {
      queued = false
      field.sync()
    })
  }
  new MutationObserver((list) => {
    for (const m of list) {
      if (m.type === 'attributes') return resync()
      for (const n of [...m.addedNodes, ...m.removedNodes])
        if (n.nodeType === 1 && (n.matches('[data-blob]') || n.querySelector('[data-blob]'))) return resync()
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-blob', 'data-blob-color', 'data-blob-motion'] })
  for (const ev of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) document.addEventListener(ev, resync)

  // Pointer for magnetism (fine pointers only).
  if (html.classList.contains('fine') && !reduced)
    addEventListener('pointermove', (e) => {
      field.mouse.x = e.clientX
      field.mouse.y = e.clientY
      field.mouse.active = true
    }, { passive: true })
}

// Normally after load and an idle moment, so the blob field never competes with the page's first paint (LCP).
// Arriving through a transition the screen is covered anyway: boot right away so the cover can flow back.
const later = () => ('requestIdleCallback' in window ? requestIdleCallback(boot, { timeout: 800 }) : setTimeout(boot, 200))
if (html.classList.contains('arrive')) boot()
else if (document.readyState === 'complete') later()
else addEventListener('load', later, { once: true })
